import { useMemo } from 'react';
import { RegisterScreen } from '@/components/RegisterScreen';
import { useRows } from '@/hooks/useResource';
import { StatusPill, formatDate, formatNumber, todayIso } from '@/components/live';
import {
  dateNotFuture, plotCell, plotField, speciesBelongsToPlot, speciesField, usePracticePickers,
} from '@/screens/parts/practiceFields';
import { HEALTH, SAPLING_LOSS_CAUSES, optionLabel } from '@/lib/practiceLists';

interface Row {
  id: string; growth_observation_code: string | null; plot_code: string | null; farmer_name: string | null; village_name: string | null;
  species_label: string | null; observed_on: string; trees_sampled: number | null; average_girth_cm: number | null;
  average_dbh_cm: number | null; average_height_m: number | null; surviving_trees: number | null; dead_trees: number | null;
  health: string | null; estimated_production: number | null; production_unit_label: string | null; expected_harvest_year: number | null;
  // Only where the record is about a batch that was issued.
  sapling_issue_id: string | null; sapling_issue_code: string | null; quantity_issued: number | null;
  planted_count: number | null; lost_count: number | null; survival_rate_pct: number | null;
  months_since_issue: number | null; loss_cause: string | null; loss_cause_detail: string | null;
  replacement_count: number | null;
}

interface IssueOption {
  id: string; sapling_issue_code: string | null; farmer_name: string; plot_id: string | null;
  plot_code: string | null; species_label: string | null; quantity_issued: number; issued_on: string;
}

/**
 * Tree growth and the production estimate, across every plot (owner, 2026-09-25: "these all make
 * as ui pages under Plot management").
 *
 * Two questions live here that no single plot record can answer. Survival: how many of what was
 * planted is still standing, which is what a plantation is judged on. And supply: what is expected
 * to come off which plots in which year, which is the number the sale side of this application
 * plans against.
 */
export default function TreeMonitoringScreen() {
  const pick = usePracticePickers();
  const thisYear = new Date().getFullYear();
  // The batches that were issued. A record naming one is a survival check of it; a record naming
  // none is a growth observation of a species line, which is what this register always was.
  const issues = useRows<IssueOption>('sapling_issues', { orderBy: { column: 'issued_on', ascending: false } });
  const issueOptions = useMemo(() => (issues.data ?? []).map((row) => ({
    id: row.id,
    label: `${row.sapling_issue_code ?? ''} · ${row.farmer_name}${row.plot_code ? ` · ${row.plot_code}` : ''}`
      + ` · ${row.quantity_issued} ${row.species_label ?? ''} · ${formatDate(row.issued_on)}`,
  })), [issues.data]);
  const issueOf = (id: unknown) => (issues.data ?? []).find((row) => row.id === id);
  const aboutABatch = (form: Record<string, unknown>) => Boolean(form.sapling_issue_id);
  // 70%: the number a planting is usually judged to have failed below. A prompt to look, not a
  // finding - the same threshold the standing-survival card already uses.
  const poorBatch = (row: Row) => row.survival_rate_pct != null && row.survival_rate_pct < 70;
  const survival = (row: Row) => {
    const total = (row.surviving_trees ?? 0) + (row.dead_trees ?? 0);
    return total > 0 ? ((row.surviving_trees ?? 0) / total) * 100 : null;
  };

  return (
    <RegisterScreen<Row>
      addLabel="Add observation" exportName="growth-observations"
      orderBy={{ column: 'observed_on', ascending: false }}
      recordLabel={(row) => `growth observation ${row.growth_observation_code ?? ''} - ${row.species_label ?? ''} on ${row.plot_code ?? ''}`}
      screenKey="plot.inventory"
      recordNoun="observation"
      searchColumns={['growth_observation_code', 'plot_code', 'farmer_name', 'species_label', 'sapling_issue_code']}
      subtitle="One walk, one record: what is alive, how it is growing, and - where it came from an issued batch - how much of it survived."
      table="ops.plot_growth_observations" title="Tree monitoring" view="plot_growth_observations"
      columns={[
        { key: 'plot_code', label: 'Plot', render: plotCell },
        { key: 'species_label', label: 'What', render: (row) => (
          <span className="cell-stack">
            <strong>{row.species_label ?? 'Issued batch'}</strong>
            {row.sapling_issue_code && (
              <small className="muted">{row.sapling_issue_code} · {row.months_since_issue} mo after issue</small>
            )}
          </span>
        ) },
        { key: 'observed_on', label: 'Observed', render: (row) => formatDate(row.observed_on) },
        { key: 'average_girth_cm', label: 'Girth / DBH', align: 'right', render: (row) => (
          [row.average_girth_cm != null ? `${formatNumber(row.average_girth_cm, 1)} cm` : null,
            row.average_dbh_cm != null ? `DBH ${formatNumber(row.average_dbh_cm, 1)}` : null].filter(Boolean).join(' · ') || '--'
        ) },
        { key: 'average_height_m', label: 'Height', align: 'right',
          render: (row) => (row.average_height_m == null ? '--' : `${formatNumber(row.average_height_m, 1)} m`) },
        { key: 'surviving_trees', label: 'Survival', align: 'right', render: (row) => {
          const rate = survival(row);
          if (row.surviving_trees == null && row.dead_trees == null) return '--';
          return (
            <span className="cell-stack">
              <strong>{formatNumber(row.surviving_trees, 0)}</strong>
              {rate != null && <small className={rate < 70 ? 'field-error' : 'muted'}>{formatNumber(rate, 0)}%</small>}
            </span>
          );
        } },
        { key: 'survival_rate_pct', label: 'Of the batch', align: 'right', render: (row) => (
          row.survival_rate_pct == null ? <span className="muted">--</span> : (
            <span className="cell-stack">
              <strong className={poorBatch(row) ? 'field-error' : undefined}>{formatNumber(row.survival_rate_pct, 1)}%</strong>
              <small className="muted">{row.lost_count} lost{row.loss_cause ? ` · ${optionLabel(SAPLING_LOSS_CAUSES, row.loss_cause)}` : ''}</small>
            </span>
          )
        ) },
        { key: 'health', label: 'Health', render: (row) => (row.health ? <StatusPill value={row.health} /> : <span className="muted">--</span>) },
        { key: 'estimated_production', label: 'Estimated yield', align: 'right', render: (row) => (
          row.estimated_production == null ? '--'
            : `${formatNumber(row.estimated_production, 2)} ${row.production_unit_label ?? ''}`.trim()
        ) },
        { key: 'expected_harvest_year', label: 'Harvest', align: 'right', render: (row) => row.expected_harvest_year ?? '--' },
      ]}
      stats={[
        { key: 'poor', label: 'Poor health', tone: 'error', match: (row) => row.health === 'poor' },
        // 70% is the number a plantation is usually replanted below; it is a prompt to look, not a
        // finding. Rows that recorded neither figure are not counted either way.
        { key: 'low_survival', label: 'Survival under 70%', tone: 'warn', match: (row) => {
          const rate = survival(row);
          return rate != null && rate < 70;
        } },
        { key: 'due', label: `Harvest due by ${thisYear + 1}`, tone: 'info',
          match: (row) => row.expected_harvest_year != null && row.expected_harvest_year <= thisYear + 1 },
        { key: 'estimated', label: 'With a yield estimate', tone: 'ok', match: (row) => row.estimated_production != null },
        { key: 'batch', label: 'Batch survival under 70%', tone: 'error', match: poorBatch },
      ]}
      defaults={{ observed_on: todayIso() }}
      fields={[
        plotField(pick.plots),
        // Naming a batch makes this a survival check of it. Everything about the batch - the
        // farmer, what was issued, when - comes from the issue and is not asked twice.
        { key: 'sapling_issue_id', label: 'Issued batch (optional)', type: 'select', options: issueOptions, wide: true,
          placeholder: issueOptions.length ? 'A growth observation, not about an issued batch' : 'No sapling has been issued yet',
          hint: 'Name a batch to record how much of it survived. Leave blank for an ordinary growth observation.' },
        // Not required when a batch is named: saplings issued three months ago are not in the
        // plot's tree inventory yet, and requiring the line would mean inventing one.
        { ...speciesField(pick, false), required: (form) => !aboutABatch(form),
          hint: 'Required unless this record is about an issued batch.' },
        { key: 'observed_on', label: 'Observed on', type: 'date', required: true, max: todayIso() },
        { key: 'trees_sampled', label: 'Trees sampled', section: 'Growth', type: 'number' },
        { key: 'health', label: 'Overall health', type: 'select', options: HEALTH },
        { key: 'average_girth_cm', label: 'Average girth (cm)', type: 'number' },
        { key: 'average_dbh_cm', label: 'Average DBH (cm)', type: 'number' },
        { key: 'average_height_m', label: 'Average height (m)', type: 'number' },
        { key: 'planted_count', label: 'Of the batch, planted', section: 'Survival', type: 'number', showIf: aboutABatch,
          hint: 'Blank when every sapling issued was planted.' },
        { key: 'surviving_trees', label: 'Alive today', section: 'Survival', type: 'number' },
        { key: 'dead_trees', label: 'Dead trees', type: 'number', showIf: (form) => !aboutABatch(form) },
        { key: 'loss_cause', label: 'Lost to', type: 'select', options: SAPLING_LOSS_CAUSES, showIf: aboutABatch,
          hint: 'Required when any were lost, and refused when none were.' },
        { key: 'loss_cause_detail', label: 'What happened', type: 'text', wide: true,
          showIf: (form) => aboutABatch(form) && Boolean(form.loss_cause) },
        { key: 'replacement_count', label: 'Replacements issued', type: 'number', showIf: aboutABatch,
          hint: 'Record these as their own sapling issue too, so the next check counts them.' },
        { key: 'expected_harvest_year', label: 'Expected harvest year', section: 'Yield', type: 'number' },
        { key: 'estimated_production', label: 'Estimated production', type: 'number' },
        { key: 'production_unit_id', label: 'Production unit', type: 'select', options: pick.units,
          hint: 'Filled from the species when a Project Admin has set its unit.',
          required: (form) => String(form.estimated_production ?? '').trim() !== '' },
        { key: 'photo_document_id', label: 'Photo', section: 'Evidence', type: 'file', accept: 'image/*' },
        { key: 'remarks', label: 'Remarks', type: 'textarea' },
      ]}
      validate={(form) => {
        const configured = pick.productionUnitOf(String(form.species_inventory_id));
        const issue = issueOf(form.sapling_issue_id);
        const planted = String(form.planted_count ?? '').trim() === '' ? null : Number(form.planted_count);
        const alive = String(form.surviving_trees ?? '').trim() === '' ? null : Number(form.surviving_trees);
        const outOf = issue ? planted ?? issue.quantity_issued : null;
        return {
          // The same refusals the database makes, answered while the form is still open rather
          // than after it is submitted.
          sapling_issue_id: issue && form.plot_id && issue.plot_id && issue.plot_id !== form.plot_id
            ? `That batch was issued for ${issue.plot_code ?? 'another plot'}.` : undefined,
          surviving_trees: issue && alive != null && outOf != null && alive > outOf
            ? `Only ${outOf} from that batch, so ${alive} cannot be alive.` : undefined,
          planted_count: issue && planted != null && planted > issue.quantity_issued
            ? `Only ${issue.quantity_issued} were issued.` : undefined,
          loss_cause: issue && alive != null && outOf != null && alive < outOf && !form.loss_cause
            ? `Say why ${outOf - alive} of ${outOf} did not survive.`
            : issue && alive != null && outOf != null && alive >= outOf && form.loss_cause
              ? 'Nothing was lost, so there is no cause to record.' : undefined,
          species_inventory_id: speciesBelongsToPlot(pick, form),
          observed_on: issue && form.observed_on && String(form.observed_on) < issue.issued_on
            ? `They were issued on ${formatDate(issue.issued_on)}.`
            : dateNotFuture(form, 'observed_on', 'The date'),
          production_unit_id: configured && form.production_unit_id && form.production_unit_id !== configured
            ? `This species' production is estimated in ${pick.units.find((unit) => unit.id === configured)?.label ?? 'its configured unit'}.` : undefined,
        };
      }}
    />
  );
}

